import { useState, useEffect, useRef, useCallback } from "react"
import { useHistory, useLocation } from "react-router-dom"
import enewspaperApi from "../../../libs/enewspaper"

export const useCustomerList = () => {
	const history = useHistory()
	const location = useLocation()
	const controllerRef = useRef(null)
	const planDropdownRef = useRef(null)

	// URL search params
	const searchParams = new URLSearchParams(location.search)

	// Filter states
	const [search, setSearch] = useState(searchParams.get("search") || "")
	const [orderIdSearch, setOrderIdSearch] = useState(searchParams.get("orderId") || searchParams.get("invoiceId") || "")
	const [searchType, setSearchType] = useState(searchParams.get("searchType") || "orderId")
	const [filterStatus, setFilterStatus] = useState(searchParams.get("status") || "all")
	const [filterRecurring, setFilterRecurring] = useState(searchParams.get("isRecurring") || "all")
	const [filterPlan, setFilterPlan] = useState(() => {
		const planParam = searchParams.get("plan")
		return planParam && planParam !== "all" ? planParam.split(",") : []
	})
	const [planCountOperator, setPlanCountOperator] = useState(searchParams.get("planCountOp") || "eq")
	const [planCountValue, setPlanCountValue] = useState(searchParams.get("planCount") || "")

	// Data states
	const [planList, setPlanList] = useState([])
	const [customers, setCustomers] = useState([])
	const [customerSchedules, setCustomerSchedules] = useState({}) // Store schedule data by customer ID
	const [loading, setLoading] = useState(true)
	const [page, setPage] = useState(parseInt(searchParams.get("page")) || 1)
	const [totalPages, setTotalPages] = useState(1)
	const [total, setTotal] = useState(0)
	const [limit, setLimit] = useState(parseInt(searchParams.get("limit")) || 10)

	// UI states
	const [isPlanDropdownOpen, setIsPlanDropdownOpen] = useState(false)

	// Update URL parameters
	const updateURLParams = useCallback(
		(params) => {
			const newSearchParams = new URLSearchParams(location.search)

			// Clear both orderId, invoiceId, and searchType first to prevent conflicts
			newSearchParams.delete("orderId")
			newSearchParams.delete("invoiceId")
			newSearchParams.delete("searchType")

			Object.entries(params).forEach(([key, value]) => {
				if (key === "plan" && Array.isArray(value) && value.length > 0) {
					newSearchParams.set(key, value.join(","))
				} else if (value && value !== "all" && value !== "" && !Array.isArray(value)) {
					newSearchParams.set(key, value)
				} else {
					newSearchParams.delete(key)
				}
			})

			const newSearch = newSearchParams.toString()
			const newURL = `${location.pathname}${newSearch ? `?${newSearch}` : ""}`
			history.replace(newURL)
		},
		[history, location]
	)

	// Fetch schedule data for recurring customers
	const fetchCustomerSchedules = useCallback(async (customers) => {
		const schedulePromises = customers
			.filter((customer) => customer.isRecurring)
			.map(async (customer) => {
				try {
					const scheduleData = await enewspaperApi.getScheduleList(customer.id)
					return { customerId: customer.id, scheduleData }
				} catch (error) {
					console.error(`Error fetching schedule for customer ${customer.id}:`, error)
					return { customerId: customer.id, scheduleData: null }
				}
			})

		const scheduleResults = await Promise.all(schedulePromises)
		const scheduleMap = {}
		scheduleResults.forEach(({ customerId, scheduleData }) => {
			scheduleMap[customerId] = scheduleData
		})
		setCustomerSchedules(scheduleMap)
	}, [])

	// Fetch customers data
	const fetchData = useCallback(
		async (searchParams = null) => {
			setLoading(true)
			if (controllerRef.current) controllerRef.current.abort()
			controllerRef.current = new AbortController()

			try {
				const params = searchParams || new URLSearchParams(location.search)
				const currentSearchType = params.get("searchType") || "orderId"
				const searchValue = params.get("orderId") || params.get("invoiceId") || ""

				const apiParams = {
					page: parseInt(params.get("page")) || page,
					limit: parseInt(params.get("limit")) || limit,
					search: params.get("search") || "",
					status: params.get("status") && params.get("status") !== "all" ? params.get("status") : undefined,
					isRecurring: params.get("isRecurring") && params.get("isRecurring") !== "all" ? params.get("isRecurring") : undefined,
					planId: params.get("plan") ? params.get("plan") : undefined,
					planCountOp: params.get("planCountOp") && params.get("planCount") ? params.get("planCountOp") : undefined,
					planCount: params.get("planCount") && params.get("planCount") !== "" ? parseInt(params.get("planCount")) : undefined,
					signal: controllerRef.current.signal,
				}

				// Add search value based on search type
				if (searchValue) {
					if (currentSearchType === "orderId") {
						apiParams.orderId = searchValue
					} else if (currentSearchType === "invoiceId") {
						apiParams.invoiceId = searchValue
					}
				}

				const data = await enewspaperApi.getCustomerList(apiParams)
				const customerData = data.customers || []
				setCustomers(customerData)
				setTotalPages(data.totalPages || 1)
				setTotal(data.total || 0)

				// Fetch schedule data for recurring customers
				if (customerData.length > 0) {
					fetchCustomerSchedules(customerData)
				}
			} catch (e) {
				if (e.name !== "AbortError") {
					console.error("Fetch error:", e)
				}
			} finally {
				setLoading(false)
			}
		},
		[page, limit, location.search, fetchCustomerSchedules]
	)

	// Fetch plan list
	const fetchPlanList = useCallback(async () => {
		try {
			const plans = await enewspaperApi.getPlanList()
			setPlanList(plans || [])
		} catch (e) {
			console.error("Fetch plans error:", e)
		}
	}, [])

	// Handle search
	const handleSearch = async () => {
		setIsPlanDropdownOpen(false)

		const newParams = {
			page: "1",
			limit: limit.toString(),
			search: search,
			status: filterStatus,
			isRecurring: filterRecurring,
			plan: filterPlan,
			planCountOp: planCountValue ? planCountOperator : undefined,
			planCount: planCountValue,
		}

		// Add the search value and searchType based on search type
		if (orderIdSearch && orderIdSearch.trim() !== "") {
			newParams.searchType = searchType
			if (searchType === "orderId") {
				newParams.orderId = orderIdSearch
			} else if (searchType === "invoiceId") {
				newParams.invoiceId = orderIdSearch
			}
		}

		updateURLParams(newParams)
		setPage(1)

		setLoading(true)
		if (controllerRef.current) controllerRef.current.abort()
		controllerRef.current = new AbortController()

		try {
			const searchParams = new URLSearchParams()
			Object.entries(newParams).forEach(([key, value]) => {
				if (key === "plan" && Array.isArray(value) && value.length > 0) {
					searchParams.set(key, value.join(","))
				} else if (value && value !== "all" && value !== "" && !Array.isArray(value)) {
					searchParams.set(key, value)
				}
			})

			await fetchData(searchParams)
		} catch (e) {
			if (e.name !== "AbortError") {
				console.error("Search error:", e)
			}
		}
	}

	// Handle page change
	const handlePageChange = (newPage) => {
		const newParams = {
			limit: limit.toString(),
			search: search,
			status: filterStatus,
			isRecurring: filterRecurring,
			plan: filterPlan,
			planCountOp: planCountValue ? planCountOperator : undefined,
			planCount: planCountValue,
			page: newPage.toString(),
		}

		// Add the search value and searchType based on search type
		if (orderIdSearch && orderIdSearch.trim() !== "") {
			newParams.searchType = searchType
			if (searchType === "orderId") {
				newParams.orderId = orderIdSearch
			} else if (searchType === "invoiceId") {
				newParams.invoiceId = orderIdSearch
			}
		}

		updateURLParams(newParams)
	}

	// Handle limit change
	const handleLimitChange = (newLimit) => {
		const newParams = {
			limit: newLimit.toString(),
			search: search,
			status: filterStatus,
			isRecurring: filterRecurring,
			plan: filterPlan,
			planCountOp: planCountValue ? planCountOperator : undefined,
			planCount: planCountValue,
			page: "1",
		}

		// Add the search value and searchType based on search type
		if (orderIdSearch && orderIdSearch.trim() !== "") {
			newParams.searchType = searchType
			if (searchType === "orderId") {
				newParams.orderId = orderIdSearch
			} else if (searchType === "invoiceId") {
				newParams.invoiceId = orderIdSearch
			}
		}

		updateURLParams(newParams)
	}

	// Effects
	useEffect(() => {
		fetchData()
		return () => controllerRef.current?.abort()
	}, [fetchData])

	useEffect(() => {
		fetchPlanList()
	}, [fetchPlanList])

	// Handle click outside dropdown
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (planDropdownRef.current && !planDropdownRef.current.contains(event.target)) {
				setIsPlanDropdownOpen(false)
			}
		}

		document.addEventListener("mousedown", handleClickOutside)
		return () => {
			document.removeEventListener("mousedown", handleClickOutside)
		}
	}, [])

	// Listen to URL changes
	useEffect(() => {
		const searchParams = new URLSearchParams(location.search)
		const currentSearchType = searchParams.get("searchType") || "orderId"
		setSearch(searchParams.get("search") || "")
		setOrderIdSearch(searchParams.get("orderId") || searchParams.get("invoiceId") || "")
		setSearchType(currentSearchType)
		setFilterStatus(searchParams.get("status") || "all")
		setFilterRecurring(searchParams.get("isRecurring") || "all")
		const planParam = searchParams.get("plan")
		setFilterPlan(planParam && planParam !== "all" ? planParam.split(",") : [])
		setPlanCountOperator(searchParams.get("planCountOp") || "eq")
		setPlanCountValue(searchParams.get("planCount") || "")
		setPage(parseInt(searchParams.get("page")) || 1)
		setLimit(parseInt(searchParams.get("limit")) || 10)
	}, [location.search])

	return {
		// Filter states
		search,
		setSearch,
		orderIdSearch,
		setOrderIdSearch,
		searchType,
		setSearchType,
		filterStatus,
		setFilterStatus,
		filterRecurring,
		setFilterRecurring,
		filterPlan,
		setFilterPlan,
		planCountOperator,
		setPlanCountOperator,
		planCountValue,
		setPlanCountValue,

		// Data states
		planList,
		customers,
		customerSchedules,
		loading,
		page,
		totalPages,
		total,
		limit,

		// UI states
		isPlanDropdownOpen,
		setIsPlanDropdownOpen,
		planDropdownRef,

		// Actions
		handleSearch,
		handlePageChange,
		handleLimitChange,
		fetchData,
	}
}

export const useAddPlanModal = () => {
	// Modal states
	const [isAddPlanModalOpen, setIsAddPlanModalOpen] = useState(false)
	const [userSearchQuery, setUserSearchQuery] = useState("")
	const [foundUsers, setFoundUsers] = useState([])
	const [selectedUser, setSelectedUser] = useState(null)
	const [selectedPlanForUser, setSelectedPlanForUser] = useState("")
	const [isSearchingUsers, setIsSearchingUsers] = useState(false)
	const [isAddingPlan, setIsAddingPlan] = useState(false)
	const [hasSearched, setHasSearched] = useState(false)

	// Search users
	const searchUsers = async (query) => {
		if (!query.trim()) {
			setFoundUsers([])
			setHasSearched(false)
			return
		}

		setIsSearchingUsers(true)
		setHasSearched(true)
		try {
			const data = await enewspaperApi.getUserMapCustomerList({
				search: query,
				limit: 20,
				page: 1,
			})
			setFoundUsers(data.users || [])
		} catch (error) {
			console.error("Error searching users:", error)
			setFoundUsers([])
		} finally {
			setIsSearchingUsers(false)
		}
	}

	// Add plan to user
	const handleAddPlanToUser = async () => {
		if (!selectedUser || !selectedPlanForUser) {
			alert("กรุณาเลือกผู้ใช้และแพ็คเกจที่ต้องการเพิ่ม")
			return
		}

		const userId = selectedUser.memberData?.id
		if (!userId) {
			alert("ไม่พบ User ID ของผู้ใช้")
			return
		}

		setIsAddingPlan(true)
		try {
			await enewspaperApi.subscribeCustomerToPlan(selectedPlanForUser, { userId })
			alert("เพิ่มแพ็คเกจสำเร็จ!")
			handleModalClose()
			return true // Signal success
		} catch (error) {
			console.error("Error adding plan:", error)
			alert("เพิ่มแพ็คเกจไม่สำเร็จ กรุณาลองใหม่")
			return false
		} finally {
			setIsAddingPlan(false)
		}
	}

	// Close modal and reset
	const handleModalClose = () => {
		setIsAddPlanModalOpen(false)
		setSelectedUser(null)
		setSelectedPlanForUser("")
		setUserSearchQuery("")
		setFoundUsers([])
		setHasSearched(false)
	}

	return {
		isAddPlanModalOpen,
		setIsAddPlanModalOpen,
		userSearchQuery,
		setUserSearchQuery,
		foundUsers,
		selectedUser,
		setSelectedUser,
		selectedPlanForUser,
		setSelectedPlanForUser,
		isSearchingUsers,
		isAddingPlan,
		hasSearched,
		searchUsers,
		handleAddPlanToUser,
		handleModalClose,
	}
}
